'use client'

import { memo, useEffect, useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { useTranslations } from 'next-intl'
import { cn, type TBaseComponentProps, useMediaQuery } from '@segway/core'
import { FormilyProductDescription } from '@segway/core/src/graphql/generated/graphql'
import mxTranslations from '@segway/core/src/i18n/messages/es-MX.json'
import { useInViewport } from 'ahooks'

import { PageBuilderRenderer } from '@/components'

import { ProductDetails, StoryblockPdp } from '../../components'
import { ProductItem, useProductData } from '../../ProductProvider'
import { RelatedProducts } from '../relatedProducts'
import { Review } from '../review'
import { SpecificationsSupport } from '../specificationsSupport'

const OwlCarousel = dynamic(() => import('@/components/owlCarousel/OwlCarousel'), {
  ssr: false,
})

export type TProductDetailProps = TBaseComponentProps

const TABS = [
  { id: 'product', label: mxTranslations?.Product?.product || 'Product' },
  { id: 'support', label: mxTranslations?.Product?.support || 'Support' },
  { id: 'reviews', label: mxTranslations?.Product?.reviews || 'Review' },
]

interface YotpoMessageType {
  isEnabled: boolean
  isRenderWidget: boolean
  isV3ReviewsWidget: boolean
  reviews_instance_id: string
  yotpo_widget_url: string
}

type TProductPageProps = TBaseComponentProps<{
  data: NonNullable<ProductItem>
  hasProductParameterSection: boolean
}>

const ProductDetail = memo(({ data, hasProductParameterSection }: TProductPageProps) => {
  const [activeTab, setActiveTab] = useState(TABS[0].id)
  const videoRef = useRef<HTMLVideoElement>(null)
  const productRef = useRef<HTMLDivElement>(null)
  const supportRef = useRef<HTMLDivElement>(null)
  const reviewsRef = useRef<HTMLDivElement>(null)
  const responsive = useMediaQuery()

  const [productViewport] = useInViewport(productRef)
  const [supportViewport] = useInViewport(supportRef)
  const [reviewsViewport] = useInViewport(reviewsRef)

  const [isPlaying, setIsPlaying] = useState(false)
  const [isHide, setIsHide] = useState(false)
  const [isVolumeOn, setIsVolumeOn] = useState(false)
  const [isTabClick, setIsTabClick] = useState(false)

  const product = useProductData()

  const isShowReviewWidget = useMemo(() => {
    return data.yotpoMessage?.isEnabled && data.yotpoMessage.isRenderWidget
  }, [data])

  const t = useTranslations('Common')

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId)
    switch (tabId) {
      case 'product':
        if (productRef.current) {
          setTimeout(() => {
            if (productRef.current) {
              window.scrollTo({
                top: productRef.current.offsetTop - 110,
                behavior: 'smooth',
              })
            }
          }, 400)
        }
        break
      case 'support':
        if (supportRef.current) {
          setTimeout(() => {
            if (supportRef.current) {
              window.scrollTo({
                top: supportRef.current.offsetTop - (responsive && responsive.xl ? 150 : 110),
                behavior: 'smooth',
              })
            }
          }, 400)
        }
        break
      case 'reviews':
        if (reviewsRef.current) {
          setTimeout(() => {
            if (reviewsRef.current) {
              window.scrollTo({
                top: reviewsRef.current.offsetTop - (responsive && responsive.xl ? 110 : 40),
                behavior: 'smooth',
              })
            }
          }, 400)
        }
        break
    }
    setIsTabClick(true)
    setTimeout(() => {
      setIsTabClick(false)
    }, 1000)
  }

  const skuClass = useMemo(() => {
    return data.sku?.split('.').join('-')
  }, [data.sku])

  useEffect(() => {
    if (!isTabClick) {
      if (productViewport && !supportViewport && !reviewsViewport) {
        setActiveTab('product')
      }
      if (supportViewport && !reviewsViewport) {
        setActiveTab('support')
      }
      if (reviewsViewport && !supportViewport && !productViewport) {
        setActiveTab('reviews')
      }
    }
  }, [productViewport, supportViewport, reviewsViewport, isTabClick])

  return (
    <div className={`pdp-page pb-10 ${hasProductParameterSection ? 'pt-[29px]' : 'pt-0'}`}>
      {/* 选项卡 */}
      <div className="sticky top-10 z-10 bg-white">
        <div className="max-page-content mx-3 flex border-b border-gray-200 p-0 pt-[17px] xl:mx-auto xl:!pt-[27px]">
          {TABS.map((tab) => {
            if (tab.id === 'reviews' && !isShowReviewWidget) {
              return null
            }

            return (
              <button
                key={tab.id}
                id={tab.id}
                className={cn(
                  'relative mr-7 pb-4 font-miSansRegular330 text-lg leading-[21px] text-black transition-colors hover:text-gray-800 xl:mr-10 xl:w-auto xl:px-10 xl:py-4 xl:text-3xl xl:leading-[29px] xl:text-gray-500',
                  {
                    'font-miSansDemiBold450 text-lg xl:text-black': activeTab === tab.id,
                  },
                )}
                onClick={() => handleTabClick(tab.id)}>
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 h-0.5 w-full bg-black" />
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* 内容区域 */}
      <div className="overflow-x-hidden max-xl:mt-[-24px]">
        <div>
          <div ref={productRef} id="pdp-description" className="pdp-description product data items">
            <div className="max-page-content">
              <div className={`pdp-info-features ${product?.feature?.html ? '' : '!mb-0'}`}>
                {product?.feature?.html && (
                  <>
                    {product.feature.html.includes('data-content-type') ? (
                      <PageBuilderRenderer content={product?.feature.html} />
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: product?.feature.html }} />
                    )}
                  </>
                )}
              </div>
            </div>
            {product?.featured_video_url && (
              <div className="max-page-content">
                <div className="pdp-video-player">
                  <video
                    src={product.featured_video_url}
                    ref={videoRef}
                    preload="auto"
                    poster={product?.featured_small_image_url ?? ''}
                    onEnded={() => {
                      setIsHide(false)
                    }}
                    className="aspect-[2/1]"></video>
                  <span
                    className={`play-btn ${isHide ? 'hide' : ''} cursor-pointer`}
                    onClick={() => {
                      setIsHide(true)
                      setIsPlaying(true)
                      setIsVolumeOn(true)
                      videoRef?.current?.play()
                    }}>
                    {t('play_video')}
                  </span>
                  <div className={`info ${isHide ? 'active' : ''}`}>
                    <span
                      className={`icon ${isPlaying ? 'pause' : 'play'} cursor-pointer`}
                      onClick={() => {
                        setIsPlaying((prev) => !prev)
                        if (isPlaying) {
                          videoRef?.current?.pause()
                        } else {
                          videoRef?.current?.play()
                        }
                      }}></span>
                    <span className="icon sound"></span>
                    <span
                      className={`icon volume ${isVolumeOn ? 'on' : ''}`}
                      onClick={() => {
                        setIsVolumeOn((prev) => !prev)
                        if (videoRef?.current) {
                          videoRef.current.muted = !videoRef?.current?.muted
                        }
                      }}></span>
                  </div>
                </div>
              </div>
            )}
            <div className="max-page-content">
              <div className={`pdp-description-content ${skuClass}`}>
                {product?.description?.html && (
                  <>
                    {product.description.html.includes('data-content-type') ? (
                      <PageBuilderRenderer content={product.description.html} />
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: product.description.html }} />
                    )}
                  </>
                )}
              </div>
            </div>
            {product?.description?.html && <OwlCarousel data={product.description.html} />}

            {data?.formily_description?.some((item) => item?.type !== 'pd_firework_video') ? (
              <ProductDetails data={data.formily_description as FormilyProductDescription[]} />
            ) : null}
          </div>

          <div
            ref={supportRef}
            id="support-section"
            className={`max-page-content ${data?.formily_description?.some((item) => item?.type !== 'pd_firework_video') ? 'mt-[28px]' : 'mt-[44px]'} xl:mt-24`}>
            <SpecificationsSupport />
          </div>

          {data?.formily_description
            ?.filter((item) => item?.type === 'pd_firework_video')
            .map((item) => (
              <div className="max-page-content mt-10" key={item?.id}>
                <StoryblockPdp blockData={item?.item_data as string} />
              </div>
            ))}

          {isShowReviewWidget && (
            <div
              className="max-page-content mt-[28px] xl:mt-24"
              ref={reviewsRef}
              id="reviews-section">
              <Review config={data.yotpoMessage as YotpoMessageType} product={product} />
            </div>
          )}

          <div className="max-page-content mt-[50px] xl:mt-[104px]">
            <RelatedProducts />
          </div>
        </div>
      </div>
    </div>
  )
})

ProductDetail.displayName = 'ProductDetail'

export default ProductDetail
